import { useCallback, useEffect, useState } from 'react';
import { channelName, supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import type { Notification } from '@/types/database';

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  remove: (id: string) => Promise<void>;
}

const PAGE_SIZE = 30;

/**
 * The current user's recent notifications. New inserts arrive via realtime
 * so the bell updates the instant a trigger fires. Mark-read flips happen
 * optimistically since the UPDATE policy is tight (only owner, only is_read).
 */
export function useNotifications(): NotificationsState {
  const { session } = useAuth();
  const userId = session?.user.id;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);

    void supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(PAGE_SIZE)
      .then(({ data, error: err }) => {
        if (!alive) return;
        if (err) {
          setError(err.message);
          setLoading(false);
          return;
        }
        setNotifications((data ?? []) as Notification[]);
        setLoading(false);
      });

    const channel = supabase
      .channel(channelName(`notifications:${userId}`))
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as Notification;
          setNotifications((prev) =>
            prev.some((n) => n.id === row.id)
              ? prev
              : [row, ...prev].slice(0, PAGE_SIZE),
          );
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as Notification;
          setNotifications((prev) =>
            prev.map((n) => (n.id === row.id ? row : n)),
          );
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.old as { id: string };
          setNotifications((prev) => prev.filter((n) => n.id !== row.id));
        },
      )
      .subscribe();

    return () => {
      alive = false;
      void supabase.removeChannel(channel);
    };
  }, [userId]);

  const markRead = useCallback(
    async (id: string) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      );
      const { error: err } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', id);
      if (err) setError(err.message);
    },
    [],
  );

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    const { error: err } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);
    if (err) setError(err.message);
  }, [userId]);

  const remove = useCallback(async (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    const { error: err } = await supabase
      .from('notifications')
      .delete()
      .eq('id', id);
    if (err) setError(err.message);
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markRead,
    markAllRead,
    remove,
  };
}
